import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  Bell,
  Calendar,
  ChevronLeft,
  ListChecks,
  Search,
  Settings2,
  Star,
  Timer,
  Unlink,
  UserCheck,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { Resource } from "@/bryntum-lib/models/Resource";
import type { Task } from "@/bryntum-lib/models/Task";
import type { Client } from "@/bryntum-lib/models/Client";
import type { Checklist } from "@/react/components/entityViews/checklist/types";
import type { Reminder } from "@/bryntum-lib/models/Reminder";
import { NotifyeeBoardUser } from "@/bryntum-lib/models/NotifyeeBoardUser";
import { ReminderRecipient } from "@/bryntum-lib/models/ReminderRecipient";
import { useUsersContext } from "../../context/UsersContext";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { ALERT_TYPES, ENTITY_TYPES } from "@/other/constants/OtherKeys.jsx";
import { WidgetParamKeys } from "@/types/entity";
import { NOTIFY_TYPE_RECORD_KEYS } from "@/react/hooks/entityHooks/useEntityOwnership";
import { NOTIFY_TYPE_COLUMN_CONTENT } from "@/react/components/entityViews/ownership/constants/ownershipConstants";
import { cn } from "@/modern/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/modern/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/modern/components/ui/dialog-shadcn";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Switch } from "@/modern/components/ui/switch";
import { NotificationTileButton } from "@/modern/components/notification/components/NotificationTileButton";
import { UserAvatar } from "@/modern/components/user-avatar";
import { mosaicLayoutsFromRelevancyScores } from "@/modern/components/notification/components/NotificationObjectMosaic";
import type { NotificationMosaicLayout } from "@/modern/components/notification/components/NotificationTile";
import {
  SURFACE_ICON_BTN_CLS,
  SURFACE_ICON_GLYPH_CLS,
  SURFACE_SEARCH_CLS,
} from "@/modern/components/notification/constants/layout";
import {
  TILE_MUTED_SURFACE_CLS,
  TILE_SURFACE_FILL_CLS,
  toneStyle,
} from "@/modern/components/notification/constants/tones";
import {
  TabEmpty,
  UsersTabDescription,
} from "../users-ui";
import {
  USERS_SUBTITLE_CLS,
  USERS_TAB_CLS,
  USERS_TITLE_CLS,
} from "../../constants/typography";
import { TasksTab } from "./TasksTab";

type SurfaceTab = "resource" | "task" | "client" | "ownership";

type ListCategory =
  | "assigned"
  | "eligible"
  | "notify"
  | "checklists"
  | "reminders";

type Drill =
  | { kind: "home" }
  | { kind: "list"; category: ListCategory }
  | { kind: "scheduled" };

type ParentType = "task" | "resource" | "client";

type CategoryDef = {
  id: ListCategory | "scheduled";
  eyebrow: string;
  description: string;
  caption?: string;
  icon: LucideIcon;
  classes: { bg: string; text: string; ring: string };
  count: number | null;
};

type ListItem = {
  id: string | number;
  eyebrow: string;
  name: string;
  faIcon: string;
  description?: string;
  badges?: string[];
  /** Checked/total progress shown larger than ordinary badges (checklists). */
  progress?: string;
  notifyChannels?: NotifyChannelBadge[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  assignedUsers?: any[];
  weight: number;
  accent?: string | null;
  toneBg?: string;
  onOpen?: () => void;
  onRemove?: () => void;
};

const SURFACE_TABS: { id: SurfaceTab; label: string; faIcon: string }[] = [
  { id: "resource", label: "Resources", faIcon: "fa-light fa-user-helmet-safety" },
  { id: "task", label: "Tasks", faIcon: "fa-light fa-swap-arrows" },
  { id: "client", label: "Clients", faIcon: "fa-light fa-building" },
  { id: "ownership", label: "Ownership", faIcon: "fa-light fa-key" },
];

const PARENT_TYPE_TO_ENTITY = {
  task: ENTITY_TYPES.TASK,
  resource: ENTITY_TYPES.RESOURCE,
  client: ENTITY_TYPES.CLIENT,
} as const;

const NOTIFY_FLAGS = [
  NOTIFY_TYPE_RECORD_KEYS.general,
  NOTIFY_TYPE_RECORD_KEYS.notes,
  NOTIFY_TYPE_RECORD_KEYS.schedule,
  NOTIFY_TYPE_RECORD_KEYS.checklist,
  NOTIFY_TYPE_RECORD_KEYS.reminder,
  NOTIFY_TYPE_RECORD_KEYS.files,
] as const;

type NotifyChannelBadge = {
  key: (typeof NOTIFY_FLAGS)[number];
  label: string;
  on: boolean;
  Icon: LucideIcon;
  onToggle: () => void;
};

const MOSAIC_SPAN_CLS: Record<NotificationMosaicLayout, string> = {
  hero: "row-span-2",
  half: "row-span-2",
  cell: "row-span-1",
};

const TILE_ACTION_BTN_CLS =
  "inline-flex size-9 items-center justify-center rounded-lg bg-foreground/15 text-foreground/90 opacity-95 transition-[background-color,opacity,transform] duration-150 ease-out hover:bg-foreground/25 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.97] motion-reduce:active:scale-100";

const TILE_BADGE_CLS =
  "inline-flex max-w-full items-center truncate rounded-full bg-foreground/20 px-2 py-0.5 text-[0.625rem] font-normal uppercase leading-none tracking-wide";

const TILE_PROGRESS_CLS =
  "inline-flex max-w-full items-center truncate rounded-full bg-foreground/25 px-3 py-1.5 text-sm font-medium tabular-nums leading-none tracking-wide sm:text-base";

function hasAnyNotify(rec: NotifyeeBoardUser): boolean {
  return NOTIFY_FLAGS.some((k) => Boolean((rec as any)[k]));
}

function notifyChannelBadges(rec: NotifyeeBoardUser): NotifyChannelBadge[] {
  return NOTIFY_FLAGS.map((key) => {
    const on = !!(rec as any)[key];
    const meta = NOTIFY_TYPE_COLUMN_CONTENT[key];
    return {
      key,
      label: meta.title,
      on,
      Icon: meta.icon,
      onToggle: () => {
        rec.set(key, !on);
      },
    };
  });
}

function resolveParentEntity(
  project: any,
  record: {
    taskId?: number | string | null;
    resourceId?: number | string | null;
    clientId?: number | string | null;
  },
): { name: string; type: ParentType; id: number | string } | undefined {
  if (!project) return undefined;
  if (record.taskId) {
    const entity = project.extraTaskStore?.getById?.(record.taskId) ?? project.taskStore?.getById?.(record.taskId);
    if (entity?.name) return { name: entity.name, type: "task", id: record.taskId };
  }
  if (record.resourceId) {
    const entity = project.resourceStore?.getById?.(record.resourceId);
    if (entity?.name) return { name: entity.name, type: "resource", id: record.resourceId };
  }
  if (record.clientId) {
    const entity = project.clientsStore?.getById?.(record.clientId) ?? project.clientStore?.getById?.(record.clientId);
    if (entity?.name) return { name: entity.name, type: "client", id: record.clientId };
  }
  return undefined;
}

function resolveBoardUsers(project: any, ids: Array<number | string> | null | undefined) {
  if (!project?.boardUsersStore || !ids?.length) return [];
  return ids
    .map((id) => project.boardUsersStore.getById(id))
    .filter(Boolean);
}

function resourceDescription(r: Resource): string | undefined {
  const job = ((r as any).jobTitle as string | undefined)?.trim();
  if (job) return job;
  const location = ((r as any).location as string | undefined)?.trim();
  return location || undefined;
}

function BackHeader({
  title,
  count,
  subtitle,
  onBack,
}: {
  title: string;
  count?: number;
  subtitle?: string;
  onBack: () => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={onBack}
        className={cn(SURFACE_ICON_BTN_CLS, "inline-flex items-center justify-center")}
        aria-label="Back"
      >
        <ChevronLeft className={SURFACE_ICON_GLYPH_CLS} aria-hidden />
      </button>
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2.5">
          <p className={cn(USERS_TITLE_CLS, "min-w-0 truncate leading-snug")}>{title}</p>
          {count != null ? (
            <span
              className="inline-flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 px-2 text-xs font-medium tabular-nums text-foreground ring-1 ring-inset ring-primary/40"
              aria-label={`${count} connections`}
            >
              {count}
            </span>
          ) : null}
        </div>
        {subtitle ? (
          <p className={cn(USERS_SUBTITLE_CLS, "mt-1")}>{subtitle}</p>
        ) : null}
      </div>
    </div>
  );
}

function ConnectionCard({
  item,
  layout,
}: {
  item: ListItem;
  layout: NotificationMosaicLayout;
}) {
  const [channelsOpen, setChannelsOpen] = useState(false);

  // Prefer limestone tone fills (identity tokens). Task hex accents stay as CSS vars for TILE_SURFACE_FILL_CLS.
  const style: CSSProperties | undefined = item.accent
    ? {
        ["--tile-accent" as string]: item.accent,
        ["--tile-strength" as string]: "88%",
      }
    : item.toneBg
      ? toneStyle(item.toneBg)
      : undefined;

  const assigned = item.assignedUsers ?? [];
  const showAvatars = assigned.length > 0;
  const notifyChannels = item.notifyChannels ?? [];
  const isNotifyCard = notifyChannels.length > 0;
  const activeNotifyCount = notifyChannels.filter((c) => c.on).length;
  const badgeLimit = layout === "cell" ? 3 : 6;
  const badges = item.badges?.slice(0, badgeLimit) ?? [];
  const overflowBadges = (item.badges?.length ?? 0) - badges.length;
  const showFooter =
    Boolean(item.progress) || badges.length > 0 || showAvatars || isNotifyCard;
  const showTopAction = Boolean(item.onRemove) || isNotifyCard;
  const entityLabel = item.eyebrow.toLowerCase();

  const openChannels = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    setChannelsOpen(true);
  };

  return (
    <div
      role={item.onOpen ? "button" : undefined}
      tabIndex={item.onOpen ? 0 : undefined}
      onClick={item.onOpen}
      onKeyDown={(e) => {
        if (!item.onOpen) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          item.onOpen();
        }
      }}
      className={cn(
        "group relative flex h-full min-h-0 flex-col overflow-hidden rounded-[1.5rem] p-4 shadow-none",
        style ? TILE_SURFACE_FILL_CLS : TILE_MUTED_SURFACE_CLS,
        item.onOpen && "cursor-pointer active:scale-[0.99] transition-transform duration-150",
        MOSAIC_SPAN_CLS[layout],
      )}
      style={style}
    >
      {showTopAction ? (
        <div className="absolute right-3 top-3 z-10 flex items-center gap-1">
          {isNotifyCard ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className={TILE_ACTION_BTN_CLS}
                  aria-label="Notify channel settings"
                  onClick={openChannels}
                >
                  <Settings2 className="size-[1.125rem] shrink-0" aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">Channel settings</TooltipContent>
            </Tooltip>
          ) : null}
          {item.onRemove ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className={TILE_ACTION_BTN_CLS}
                  aria-label="Remove connection"
                  onClick={(e) => {
                    e.stopPropagation();
                    item.onRemove?.();
                  }}
                >
                  <Unlink className="size-[1.125rem] shrink-0" aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">Remove connection</TooltipContent>
            </Tooltip>
          ) : null}
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className={cn("flex min-w-0 shrink-0 items-center gap-2", showTopAction && "pr-12")}>
          <i className={cn(item.faIcon, "text-base leading-none opacity-70")} aria-hidden />
          <p className="m-0 min-w-0 truncate text-sm font-normal uppercase leading-none tracking-wide opacity-80">
            {item.eyebrow}
          </p>
        </div>

        {/* shrink-0 + line-clamp so rows never mid-clip glyphs when mosaic height is tight */}
        <div className="mt-2 shrink-0 space-y-1">
          <p
            title={item.name}
            className={cn(
              "m-0 min-w-0 break-words font-normal leading-snug tracking-tight",
              layout === "hero"
                ? "line-clamp-2 text-2xl"
                : layout === "half"
                  ? "line-clamp-2 text-xl"
                  : item.description
                    ? "line-clamp-1 text-lg"
                    : "line-clamp-2 text-lg",
            )}
          >
            {item.name}
          </p>
          {item.description ? (
            <p
              title={item.description}
              className={cn(
                "m-0 min-w-0 break-words text-sm font-normal leading-snug opacity-80",
                layout === "cell" ? "line-clamp-1" : "line-clamp-2",
              )}
            >
              {item.description}
            </p>
          ) : null}
        </div>
      </div>

      {showFooter ? (
        <div className="mt-auto flex min-w-0 shrink-0 items-end justify-between gap-2 pt-3">
          {isNotifyCard ? (
            <button
              type="button"
              className={cn(
                TILE_PROGRESS_CLS,
                "transition-colors duration-150 hover:bg-foreground/35",
              )}
              aria-label={`${activeNotifyCount} of ${notifyChannels.length} channels on. Open settings.`}
              onClick={openChannels}
            >
              {activeNotifyCount} {activeNotifyCount === 1 ? "channel" : "channels"}
            </button>
          ) : item.progress || badges.length > 0 ? (
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              {item.progress ? (
                <span className={TILE_PROGRESS_CLS}>{item.progress}</span>
              ) : null}
              {badges.map((badge) => (
                <span key={badge} className={TILE_BADGE_CLS}>
                  {badge}
                </span>
              ))}
              {overflowBadges > 0 ? (
                <span className={TILE_BADGE_CLS}>+{overflowBadges}</span>
              ) : null}
            </div>
          ) : (
            <span />
          )}

          {showAvatars ? (
            <div
              className="flex shrink-0 items-center -space-x-1.5"
              aria-label={`${assigned.length} assigned`}
              onClick={(e) => e.stopPropagation()}
            >
              {assigned.slice(0, 4).map((user) => (
                <UserAvatar
                  key={user.id}
                  user={user}
                  size="small"
                  showTooltip
                  showRemove={false}
                />
              ))}
              {assigned.length > 4 ? (
                <span className="relative z-[1] flex h-7 min-w-7 items-center justify-center rounded-full bg-muted px-1.5 text-xs text-muted-foreground ring-2 ring-background">
                  +{assigned.length - 4}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {isNotifyCard ? (
        <Dialog open={channelsOpen} onOpenChange={setChannelsOpen}>
          <DialogContent
            showCloseButton={false}
            className={cn(
              "flex max-h-[min(90vh,36rem)] w-full max-w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden",
              "border-border/60 bg-background p-0 shadow-2xl",
              "rounded-[2rem] sm:max-w-lg",
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <DialogTitle className="sr-only">{item.name} notification channels</DialogTitle>
            <DialogDescription className="sr-only">
              Toggle which notification channels this user receives for {item.name}.
            </DialogDescription>

            <div className="flex shrink-0 items-start gap-3 px-5 pt-5 sm:px-7">
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-2.5">
                  <i
                    className={cn(item.faIcon, "text-base leading-none text-muted-foreground")}
                    aria-hidden
                  />
                  <h2 className="min-w-0 truncate text-xl font-normal tracking-tight text-foreground sm:text-2xl">
                    {item.name}
                  </h2>
                  <span className="inline-flex h-6 shrink-0 items-center rounded-full bg-primary/10 px-2 text-xs font-medium tabular-nums text-foreground ring-1 ring-inset ring-primary/40">
                    {activeNotifyCount}/{notifyChannels.length}
                  </span>
                </div>
                <p className="mt-0.5 text-sm font-normal text-muted-foreground">
                  Notification channels
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setChannelsOpen(false)}
                aria-label="Close"
                className="size-9 shrink-0 rounded-full border-input bg-muted text-foreground/80 shadow-sm hover:border-input hover:text-foreground"
              >
                <X className="size-4" />
              </Button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 pt-5 sm:px-7">
              <ul className="space-y-1 pb-1">
                {notifyChannels.map((ch) => {
                  const ChannelIcon = ch.Icon;
                  const meta = NOTIFY_TYPE_COLUMN_CONTENT[ch.key];
                  const detail =
                    typeof meta.description === "function"
                      ? meta.description(entityLabel)
                      : ch.label;
                  return (
                    <li
                      key={ch.key}
                      className="flex items-center justify-between gap-3 rounded-2xl px-3 py-3 transition-colors duration-150 hover:bg-muted/40"
                    >
                      <div className="flex min-w-0 items-start gap-3">
                        <span className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground ring-1 ring-inset ring-border/50">
                          <ChannelIcon className="size-4" aria-hidden />
                        </span>
                        <div className="min-w-0">
                          <p className="m-0 text-sm font-medium text-foreground">{ch.label}</p>
                          <p className="m-0 mt-0.5 text-xs leading-snug text-muted-foreground">
                            {detail}
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={ch.on}
                        onCheckedChange={() => ch.onToggle()}
                        aria-label={`${ch.on ? "Disable" : "Enable"} ${ch.label}`}
                      />
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="flex shrink-0 items-center justify-end gap-2 px-5 pb-6 pt-5 sm:px-7">
              <Button
                type="button"
                variant="outline"
                onClick={() => setChannelsOpen(false)}
                className="h-9 rounded-full px-3 text-sm font-normal"
              >
                Done
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}

function ConnectionMosaic({ items }: { items: ListItem[] }) {
  const layouts = useMemo(
    () => mosaicLayoutsFromRelevancyScores(items.map((i) => i.weight)),
    [items],
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 auto-rows-[10.5rem] gap-3">
      {items.map((item, i) => (
        <ConnectionCard
          key={String(item.id)}
          item={item}
          layout={layouts[i] ?? "cell"}
        />
      ))}
    </div>
  );
}

const CATEGORY_EYEBROW_LARGE_CLS =
  "text-xs font-bold leading-none tracking-[0.16em] text-foreground sm:text-sm";
const CATEGORY_EYEBROW_COMPACT_CLS =
  "text-[0.625rem] font-bold leading-none tracking-[0.16em] text-foreground sm:text-xs";
const CATEGORY_DESCRIPTION_CLS =
  "text-xs font-normal leading-snug text-foreground/75 sm:text-sm";
const CATEGORY_CAPTION_LARGE_CLS = "text-sm leading-snug sm:text-base";
const CATEGORY_CAPTION_COMPACT_CLS = "text-sm leading-snug";
const CATEGORY_VALUE_LARGE_CLS = "text-3xl font-normal tracking-tight sm:text-4xl";
const CATEGORY_VALUE_COMPACT_CLS = "text-2xl font-normal tracking-tight sm:text-3xl";
const CATEGORY_TILE_CLS = "h-full min-h-0 p-4 [&>div:last-child]:pt-4";

function categoryWeight(cat: CategoryDef): number {
  // Scheduled has no numeric count — keep it in the mosaic without winning size by default.
  if (cat.count == null) return 0.5;
  return Math.max(0, cat.count);
}

/**
 * Updates-summary style masonry for category tiles:
 * highest count grows tall (row-span-2); peers stack beside it.
 */
function CategoryMosaic({
  categories,
  onOpen,
}: {
  categories: CategoryDef[];
  onOpen: (cat: CategoryDef) => void;
}) {
  const ordered = useMemo(() => {
    if (categories.length === 0) return [] as Array<CategoryDef & { hero: boolean }>;
    let heroIdx = 0;
    let best = categoryWeight(categories[0]);
    for (let i = 1; i < categories.length; i++) {
      const w = categoryWeight(categories[i]);
      if (w > best) {
        best = w;
        heroIdx = i;
      }
    }
    // Near-ties stay equal peers (no artificial hero).
    const weights = categories.map(categoryWeight);
    const min = Math.min(...weights);
    const max = Math.max(...weights);
    const hasHero = categories.length >= 2 && max - min >= 1;
    return categories.map((cat, i) => ({
      ...cat,
      hero: hasHero && i === heroIdx,
    }));
  }, [categories]);

  if (ordered.length === 0) return null;

  if (ordered.length === 1) {
    const cat = ordered[0];
    return (
      <div className="grid min-h-[9.5rem] max-w-xl grid-cols-1 gap-3 sm:min-h-[11rem]">
        <NotificationTileButton
          onClick={() => onOpen(cat)}
          className={CATEGORY_TILE_CLS}
          big
          eyebrowClassName={CATEGORY_EYEBROW_LARGE_CLS}
          descriptionClassName={CATEGORY_DESCRIPTION_CLS}
          captionClassName={CATEGORY_CAPTION_LARGE_CLS}
          valueClassName={CATEGORY_VALUE_LARGE_CLS}
          classes={cat.classes}
          eyebrow={cat.eyebrow}
          description={cat.description}
          value={cat.count == null ? "Open" : String(cat.count)}
          caption={cat.caption}
          icon={cat.icon}
        />
      </div>
    );
  }

  const hero = ordered.find((c) => c.hero);
  const peers = ordered.filter((c) => !c.hero);

  // 2-up with a clear winner → summary column (tall left + stack right), else equal peers.
  if (hero && peers.length >= 1) {
    return (
      <div className="grid min-h-[13.5rem] grid-cols-1 gap-3 sm:grid-cols-2 sm:grid-rows-[minmax(0,1fr)_minmax(0,1fr)]">
        <NotificationTileButton
          onClick={() => onOpen(hero)}
          className={cn(CATEGORY_TILE_CLS, "sm:row-span-2")}
          big
          eyebrowClassName={CATEGORY_EYEBROW_LARGE_CLS}
          descriptionClassName={CATEGORY_DESCRIPTION_CLS}
          captionClassName={CATEGORY_CAPTION_LARGE_CLS}
          valueClassName={CATEGORY_VALUE_LARGE_CLS}
          classes={hero.classes}
          eyebrow={hero.eyebrow}
          description={hero.description}
          value={hero.count == null ? "Open" : String(hero.count)}
          caption={hero.caption}
          icon={hero.icon}
        />
        {peers.map((cat) => (
          <NotificationTileButton
            key={cat.id}
            onClick={() => onOpen(cat)}
            className={CATEGORY_TILE_CLS}
            eyebrowClassName={CATEGORY_EYEBROW_COMPACT_CLS}
            descriptionClassName={CATEGORY_DESCRIPTION_CLS}
            captionClassName={CATEGORY_CAPTION_COMPACT_CLS}
            valueClassName={CATEGORY_VALUE_COMPACT_CLS}
            classes={cat.classes}
            eyebrow={cat.eyebrow}
            description={cat.description}
            value={cat.count == null ? "Open" : String(cat.count)}
            caption={cat.caption}
            icon={cat.icon}
          />
        ))}
      </div>
    );
  }

  // Equal peers — 2-col masonry cells sized by relevancy scores.
  const layouts = mosaicLayoutsFromRelevancyScores(ordered.map(categoryWeight));
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 auto-rows-[minmax(8rem,auto)]">
      {ordered.map((cat, i) => {
        const layout = layouts[i] ?? "cell";
        const tall = layout === "hero" || layout === "half";
        return (
          <NotificationTileButton
            key={cat.id}
            onClick={() => onOpen(cat)}
            className={cn(CATEGORY_TILE_CLS, tall && "sm:row-span-2")}
            big={tall}
            eyebrowClassName={tall ? CATEGORY_EYEBROW_LARGE_CLS : CATEGORY_EYEBROW_COMPACT_CLS}
            descriptionClassName={CATEGORY_DESCRIPTION_CLS}
            captionClassName={tall ? CATEGORY_CAPTION_LARGE_CLS : CATEGORY_CAPTION_COMPACT_CLS}
            valueClassName={tall ? CATEGORY_VALUE_LARGE_CLS : CATEGORY_VALUE_COMPACT_CLS}
            classes={cat.classes}
            eyebrow={cat.eyebrow}
            description={cat.description}
            value={cat.count == null ? "Open" : String(cat.count)}
            caption={cat.caption}
            icon={cat.icon}
          />
        );
      })}
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function OwnershipTab({ user }: { user: any }) {
  const { project } = useUsersContext();
  const { entityManager } = useSwivuBryntumContext();
  const [surface, setSurface] = useState<SurfaceTab>("resource");
  const [drill, setDrill] = useState<Drill>({ kind: "home" });
  const [listQuery, setListQuery] = useState("");

  useEffect(() => {
    setListQuery("");
  }, [drill, surface]);

  const { version: resourceVersion } = useSwivuStore({ storeId: STORE_KEYS.resourceStore });
  const { version: notifyVersion } = useSwivuStore({ storeId: STORE_KEYS.notifyeeBoardUsers });
  const { version: reminderRecipientVersion } = useSwivuStore({ storeId: STORE_KEYS.reminderRecipientStore });
  const { version: reminderVersion } = useSwivuStore({ storeId: STORE_KEYS.reminderStore });

  const goHome = useCallback(() => setDrill({ kind: "home" }), []);
  const goList = useCallback((category: ListCategory) => setDrill({ kind: "list", category }), []);
  const goScheduled = useCallback(() => setDrill({ kind: "scheduled" }), []);

  const openEntity = useCallback(
    (
      type: ParentType,
      id: string | number,
      otherParams?: Record<string, number | string | boolean>,
    ) => {
      entityManager?.openEntityManager({
        entityType: PARENT_TYPE_TO_ENTITY[type],
        entityId: id,
        ...(otherParams ? { otherParams } : {}),
      });
    },
    [entityManager],
  );

  const uid = user.id == null ? null : Number(user.id);

  // ── Resource links ────────────────────────────────────────────────────────
  const resourceData = useMemo(() => {
    const empty = {
      assigned: [] as Resource[],
      eligible: [] as Resource[],
      notify: [] as { resource: Resource; rec: NotifyeeBoardUser }[],
    };
    if (!project || uid == null) return empty;

    const all: Resource[] = (project.resourceStore as any)?.allRecords
      ?? (project.resourceStore as any)?.records
      ?? [];

    const assigned = all.filter(
      (r) => Array.isArray(r.assignedBoardUsers) && r.assignedBoardUsers.map(Number).includes(uid),
    );
    const eligible = all.filter(
      (r) => Array.isArray(r.eligibleBoardUsers) && r.eligibleBoardUsers.map(Number).includes(uid),
    );

    const notifyStore = (project as any).notfiyeeBoardUserStore;
    const notifyRecs: NotifyeeBoardUser[] =
      notifyStore?.query?.((r: NotifyeeBoardUser) => Number(r.boardUserId) === uid, true) ?? [];

    const notify: { resource: Resource; rec: NotifyeeBoardUser }[] = [];
    for (const rec of notifyRecs) {
      if (!hasAnyNotify(rec) || rec.resourceId == null) continue;
      const resource = project.resourceStore.getById(rec.resourceId) as Resource | undefined;
      if (resource) notify.push({ resource, rec });
    }

    return { assigned, eligible, notify };
  }, [project, uid, resourceVersion, notifyVersion]);

  // ── Task notify ───────────────────────────────────────────────────────────
  const taskNotify = useMemo(() => {
    if (!project || uid == null) return [] as { task: Task; rec: NotifyeeBoardUser }[];
    const notifyStore = (project as any).notfiyeeBoardUserStore;
    const notifyRecs: NotifyeeBoardUser[] =
      notifyStore?.query?.((r: NotifyeeBoardUser) => Number(r.boardUserId) === uid, true) ?? [];
    const out: { task: Task; rec: NotifyeeBoardUser }[] = [];
    for (const rec of notifyRecs) {
      if (!hasAnyNotify(rec) || rec.taskId == null) continue;
      const task =
        (project as any).extraTaskStore?.getById?.(rec.taskId) ??
        (project as any).taskStore?.getById?.(rec.taskId);
      if (task) out.push({ task: task as Task, rec });
    }
    return out;
  }, [project, uid, notifyVersion]);

  // ── Client notify ─────────────────────────────────────────────────────────
  const clientNotify = useMemo(() => {
    if (!project || uid == null) return [] as { client: Client; rec: NotifyeeBoardUser }[];
    const notifyStore = (project as any).notfiyeeBoardUserStore;
    const notifyRecs: NotifyeeBoardUser[] =
      notifyStore?.query?.((r: NotifyeeBoardUser) => Number(r.boardUserId) === uid, true) ?? [];
    const out: { client: Client; rec: NotifyeeBoardUser }[] = [];
    for (const rec of notifyRecs) {
      if (!hasAnyNotify(rec) || rec.clientId == null) continue;
      const client =
        (project as any).clientsStore?.getById?.(rec.clientId) ??
        (project as any).clientStore?.getById?.(rec.clientId);
      if (client) out.push({ client: client as Client, rec });
    }
    return out;
  }, [project, uid, notifyVersion]);

  // ── Ownership: assigned checklists + reminder notifyees ───────────────────
  const checklists: Checklist[] = [];

  const reminders = useMemo(() => {
    if (!project || uid == null) return [] as { reminder: Reminder; recipient: ReminderRecipient }[];
    const recipients: ReminderRecipient[] =
      project.reminderRecipientStore.query?.(
        (r: ReminderRecipient) => Number(r.boardUserId) === uid && !r.archived,
        true,
      ) ?? [];
    const out: { reminder: Reminder; recipient: ReminderRecipient }[] = [];
    const seen = new Set<string | number>();
    for (const recipient of recipients) {
      const reminder = project.reminderStore.getById(recipient.reminderId) as Reminder | undefined;
      // Match RemindersPage / timeline: only Reminder-type rows, not every alert with recipients.
      if (
        !reminder ||
        reminder.archived ||
        reminder.alertType !== ALERT_TYPES.REMINDER ||
        reminder.alertTag !== "Reminder"
      ) {
        continue;
      }
      if (seen.has(reminder.id)) continue;
      seen.add(reminder.id);
      out.push({ reminder, recipient });
    }
    return out;
  }, [project, uid, reminderRecipientVersion, reminderVersion]);

  // ── Category cards per surface ────────────────────────────────────────────
  const categories = useMemo((): CategoryDef[] => {
    if (surface === "resource") {
      return [
        {
          id: "assigned",
          eyebrow: "Assigned",
          description: "Resources this user is assigned to",
          caption: "Resources",
          icon: UserCheck,
          classes: { bg: "bg-cobalt", text: "text-foreground", ring: "ring-cobalt/20" },
          count: resourceData.assigned.length,
        },
        {
          id: "eligible",
          eyebrow: "Points eligible",
          description: "Resources where this user can earn points",
          caption: "Resources",
          icon: Star,
          classes: { bg: "bg-success", text: "text-foreground", ring: "ring-success/20" },
          count: resourceData.eligible.length,
        },
        {
          id: "notify",
          eyebrow: "Notify",
          description: "Resources this user receives updates for",
          caption: "Resources",
          icon: Bell,
          classes: { bg: "bg-plum", text: "text-foreground", ring: "ring-plum/20" },
          count: resourceData.notify.length,
        },
      ];
    }
    if (surface === "task") {
      return [
        {
          id: "notify",
          eyebrow: "Notify",
          description: "Tasks this user receives updates for",
          caption: "Tasks",
          icon: Bell,
          classes: { bg: "bg-plum", text: "text-foreground", ring: "ring-plum/20" },
          count: taskNotify.length,
        },
        {
          id: "scheduled",
          eyebrow: "Scheduled",
          description: "Tasks on this user's schedule",
          icon: Calendar,
          classes: { bg: "bg-success", text: "text-foreground", ring: "ring-success/20" },
          count: null,
        },
      ];
    }
    if (surface === "client") {
      return [
        {
          id: "notify",
          eyebrow: "Notify",
          description: "Clients this user receives updates for",
          caption: "Clients",
          icon: Bell,
          classes: { bg: "bg-plum", text: "text-foreground", ring: "ring-plum/20" },
          count: clientNotify.length,
        },
      ];
    }
    return [
      {
        id: "checklists",
        eyebrow: "Checklists",
        description: "Checklists this user is assigned to work on",
        caption: "Assigned work",
        icon: ListChecks,
        classes: { bg: "bg-action", text: "text-foreground", ring: "ring-action/20" },
        count: checklists.length,
      },
      {
        id: "reminders",
        eyebrow: "Reminders",
        description: "Reminders where this user is a notifyee",
        caption: "Reminders",
        icon: Timer,
        classes: { bg: "bg-warning", text: "text-foreground", ring: "ring-warning/20" },
        count: reminders.length,
      },
    ];
  }, [surface, resourceData, taskNotify, clientNotify, checklists, reminders]);

  // ── Build list items for current drill ────────────────────────────────────
  const listItems = useMemo((): ListItem[] => {
    if (drill.kind !== "list" || uid == null) return [];

    if (surface === "resource" && drill.category === "assigned") {
      return resourceData.assigned.map((r) => {
        return {
          id: r.id,
          eyebrow: "Resource",
          name: r.name ?? "Untitled",
          faIcon: "fa-light fa-user-helmet-safety",
          description: resourceDescription(r),
          // Equal mosaic cells — no size-by-assignee-count for Assigned.
          weight: 1,
          toneBg: "bg-cobalt",
          onOpen: () => openEntity("resource", r.id),
          onRemove: () => {
            const next = (r.assignedBoardUsers ?? []).map(Number).filter((id: number) => id !== uid);
            r.set("assignedBoardUsers", next);
          },
        };
      });
    }

    if (surface === "resource" && drill.category === "eligible") {
      return resourceData.eligible.map((r) => {
        return {
          id: r.id,
          eyebrow: "Resource",
          name: r.name ?? "Untitled",
          faIcon: "fa-light fa-user-helmet-safety",
          description: resourceDescription(r),
          // Equal mosaic cells — no size-by-eligible-count for Points eligible.
          weight: 1,
          toneBg: "bg-success",
          onOpen: () => openEntity("resource", r.id),
          onRemove: () => {
            const next = (r.eligibleBoardUsers ?? []).map(Number).filter((id: number) => id !== uid);
            r.set("eligibleBoardUsers", next);
          },
        };
      });
    }

    if (surface === "resource" && drill.category === "notify") {
      return resourceData.notify.map(({ resource: r, rec }) => {
        const channels = notifyChannelBadges(rec);
        const onCount = channels.filter((c) => c.on).length;
        return {
          id: r.id,
          eyebrow: "Resource",
          name: r.name ?? "Untitled",
          faIcon: "fa-light fa-user-helmet-safety",
          description: resourceDescription(r),
          notifyChannels: channels,
          weight: Math.max(1, onCount),
          toneBg: "bg-plum",
          onOpen: () => openEntity("resource", r.id),
        };
      });
    }

    if (surface === "task" && drill.category === "notify") {
      return taskNotify.map(({ task, rec }) => {
        const channels = notifyChannelBadges(rec);
        const onCount = channels.filter((c) => c.on).length;
        return {
          id: task.id,
          eyebrow: "Task",
          name: task.name ?? "Untitled",
          faIcon: "fa-light fa-swap-arrows",
          notifyChannels: channels,
          weight: Math.max(1, onCount),
          accent: (task as any).eventColor || (task as any).color || null,
          toneBg: "bg-plum",
          onOpen: () => openEntity("task", task.id),
        };
      });
    }

    if (surface === "client" && drill.category === "notify") {
      return clientNotify.map(({ client, rec }) => {
        const channels = notifyChannelBadges(rec);
        const onCount = channels.filter((c) => c.on).length;
        return {
          id: client.id,
          eyebrow: "Client",
          name: client.name ?? "Untitled",
          faIcon: "fa-light fa-building",
          notifyChannels: channels,
          weight: Math.max(1, onCount),
          toneBg: "bg-plum",
          onOpen: () => openEntity("client", client.id),
        };
      });
    }

    if (surface === "ownership" && drill.category === "checklists") {
      return [...checklists]
        .map((c) => {
          const checked = Number(c.checkedItems ?? 0);
          const unchecked = Number(c.uncheckedItemsCount ?? 0);
          const total = checked + unchecked;
          const assignedUsers = resolveBoardUsers(project, c.assignedBoardUsers);
          const recipientCount = assignedUsers.length;
          // Fully checked with items → complete; empty or remaining work → incomplete.
          const isComplete = total > 0 && unchecked === 0;
          return { c, checked, unchecked, total, assignedUsers, recipientCount, isComplete };
        })
        .sort((a, b) => {
          if (a.isComplete !== b.isComplete) return a.isComplete ? 1 : -1;
          if (b.recipientCount !== a.recipientCount) return b.recipientCount - a.recipientCount;
          return b.unchecked - a.unchecked;
        })
        .map(({ c, checked, total, assignedUsers, recipientCount }) => {
          const parent = resolveParentEntity(project, c);
          return {
            id: c.id,
            eyebrow: "Checklist",
            name: c.title ?? "Untitled",
            faIcon: "fa-light fa-list-check",
            description: parent?.name,
            progress: total > 0 ? `${checked}/${total}` : undefined,
            assignedUsers,
            // Mosaic span from assigned/notify recipient count (squared for clearer tiers).
            weight: Math.max(1, recipientCount * recipientCount),
            toneBg: "bg-action",
            onOpen: parent
              ? () => openEntity(parent.type, parent.id, { [WidgetParamKeys.CHECKLIST.EDIT]: Number(c.id) })
              : undefined,
            onRemove: () => {
              const next = (c.assignedBoardUsers ?? []).map(Number).filter((id: number) => id !== uid);
              c.assignedBoardUsers = next;
            },
          };
        });
    }

    if (surface === "ownership" && drill.category === "reminders") {
      return [...reminders]
        .sort((a, b) => {
          const aMs = a.reminder.alertDate ? new Date(a.reminder.alertDate).getTime() : 0;
          const bMs = b.reminder.alertDate ? new Date(b.reminder.alertDate).getTime() : 0;
          if (bMs !== aMs) return bMs - aMs;
          return Number(b.reminder.id) - Number(a.reminder.id);
        })
        .map(({ reminder, recipient }) => {
          const parent = resolveParentEntity(project, reminder as any);
          const allRecipientIds = ((reminder.recipients ?? []) as ReminderRecipient[])
            .filter((r) => !r.archived)
            .map((r) => r.boardUserId);
          const notifyUsers = resolveBoardUsers(project, allRecipientIds);

          let accent: string | null = null;
          if (reminder.taskId) {
            const task =
              (project as any)?.extraTaskStore?.getById?.(reminder.taskId) ??
              (project as any)?.taskStore?.getById?.(reminder.taskId);
            accent = (task as any)?.eventColor || (task as any)?.color || null;
          }

          const description =
            typeof reminder.description === "string" ? reminder.description.trim() : "";
          const title = typeof reminder.title === "string" ? reminder.title.trim() : "";
          const entityName =
            typeof parent?.name === "string" ? parent.name.trim() : "";

          return {
            id: reminder.id,
            eyebrow: "Reminder",
            name: description || title || entityName || "Reminder",
            faIcon: "fa-light fa-bell",
            description: entityName && (description || title) ? entityName : undefined,
            assignedUsers: notifyUsers,
            weight: Math.max(1, notifyUsers.length),
            accent,
            toneBg: accent ? undefined : "bg-warning",
            onOpen: parent
              ? () => openEntity(parent.type, parent.id, { [WidgetParamKeys.REMINDER.EDIT]: Number(reminder.id) })
              : undefined,
            onRemove: () => recipient.set("archived", true),
          };
        });
    }

    return [];
  }, [
    drill,
    surface,
    uid,
    project,
    resourceData,
    taskNotify,
    clientNotify,
    checklists,
    reminders,
    openEntity,
  ]);

  const listMeta = useMemo(() => {
    if (drill.kind !== "list") return null;
    return categories.find((c) => c.id === drill.category) ?? null;
  }, [drill, categories]);

  const filteredListItems = useMemo(() => {
    const q = listQuery.trim().toLowerCase();
    if (!q) return listItems;
    return listItems.filter((item) => {
      const hay = [item.name, item.description, item.eyebrow, ...(item.badges ?? [])]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [listItems, listQuery]);

  const onSurfaceChange = (next: SurfaceTab) => {
    setSurface(next);
    setDrill({ kind: "home" });
  };

  return (
    <div className="space-y-5">
      <UsersTabDescription className="mt-0 max-w-none whitespace-nowrap">
        The user's relationships and connections, including assignments, notifications, and ownership.
      </UsersTabDescription>

      <nav className="border-b border-border/70">
        <div className="flex gap-5 overflow-x-auto overflow-y-hidden">
          {SURFACE_TABS.map((t) => {
            const on = t.id === surface;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => onSurfaceChange(t.id)}
                className={cn(
                  "inline-flex items-center gap-1.5 pb-2.5 pt-0.5 transition-colors border-b-2 -mb-px whitespace-nowrap",
                  USERS_TAB_CLS,
                  on
                    ? "text-foreground border-primary"
                    : "text-muted-foreground hover:text-foreground border-transparent",
                )}
              >
                <i className={cn(t.faIcon, "shrink-0 opacity-80 text-base")} aria-hidden />
                {t.label}
              </button>
            );
          })}
        </div>
      </nav>

      <AnimatePresence mode="wait" initial={false}>
        {drill.kind === "home" ? (
          <motion.div
            key={`home-${surface}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
          >
            <CategoryMosaic
              categories={categories}
              onOpen={(cat) => {
                if (cat.id === "scheduled") goScheduled();
                else goList(cat.id);
              }}
            />
          </motion.div>
        ) : drill.kind === "scheduled" ? (
          <motion.div
            key="scheduled"
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -8 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-4"
          >
            <TasksTab
              user={user}
              compact
              headerLeading={
                <BackHeader
                  title="Scheduled"
                  subtitle="Tasks on this user's schedule"
                  onBack={goHome}
                />
              }
            />
          </motion.div>
        ) : (
          <motion.div
            key={`list-${surface}-${drill.category}`}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -8 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-4"
          >
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <BackHeader
                  title={listMeta?.eyebrow ?? "Connections"}
                  count={filteredListItems.length}
                  onBack={goHome}
                />
              </div>
              <label className="relative block w-[min(16rem,42vw)] shrink-0">
                <span className="sr-only">Search connections</span>
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <input
                  value={listQuery}
                  onChange={(e) => setListQuery(e.target.value)}
                  placeholder="Search…"
                  aria-label="Search connections"
                  className={cn(SURFACE_SEARCH_CLS, "w-full")}
                />
                {listQuery ? (
                  <button
                    type="button"
                    onClick={() => setListQuery("")}
                    className="absolute right-2 top-1/2 inline-flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors duration-150 hover:bg-foreground/10 hover:text-foreground"
                    aria-label="Clear search"
                  >
                    <X className="size-3.5" aria-hidden />
                  </button>
                ) : null}
              </label>
            </div>

            {listItems.length === 0 ? (
              <TabEmpty
                icon="fa-light fa-link"
                message={`No ${listMeta?.eyebrow?.toLowerCase() ?? "connections"}`}
                hint={listMeta?.description}
              />
            ) : filteredListItems.length === 0 ? (
              <TabEmpty
                icon="fa-light fa-magnifying-glass"
                message={`No results for "${listQuery.trim()}"`}
                hint="Try a different name or clear the search."
              />
            ) : (
              <ConnectionMosaic items={filteredListItems} />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
